import type { GlbHierarchyNode } from '../../imported-model/loaders/glb'
import type { ModelAssetNode } from '../assets/model-asset-types'
import type { ProjectModelSceneBinding } from '../loading/project-model-load-session'
import {
  listAssemblyDocumentChildNodes,
} from '../assembly-document/assembly-document-operations'

/**
 * 项目模型节点运行时状态解析器。
 *
 * 该类是项目节点、工作装配文档节点与场景对象运行时关系的唯一构建入口。普通节点使用项目组合器
 * 已明确维护的 `byProjectNodeKey`，离线包物化节点在父装配作用域内使用稳定 `instance_id`
 * 关联工作文档，complete 节点再由当前选中 GLB 根补充；状态发布后消费者只查询已确认的 binding。
 */
export class ProjectModelRuntimeStateResolver {
  /**
   * 从已发布状态解析节点的最终运行时装配身份。
   *
   * canonical key 是首选入口；complete 归档物化树与工作装配文档 key 空间不同时，
   * 才回退到 finalization 阶段建立的反向索引。
   *
   * @param state 当前已发布的项目运行时状态。
   * @param nodeKey canonical 项目节点 key。
   * @param assemblyDocumentNodeKey 可选工作装配文档节点 key。
   * @returns 已绑定的运行时装配节点 ID；节点尚未物化时返回 null。
   */
  public resolveRuntimeAssemblyNodeId(
    state: PuppyCAD.ProjectModelRuntimeState | undefined,
    nodeKey: string,
    assemblyDocumentNodeKey?: string,
  ): string | null {
    if (!state) {
      return null
    }
    const canonicalBinding = state.bindingsByNodeKey.get(nodeKey)
    if (canonicalBinding?.runtimeAssemblyNodeId) {
      return canonicalBinding.runtimeAssemblyNodeId
    }

    const documentNodeKey = assemblyDocumentNodeKey?.trim() || nodeKey
    return state.bindingsByAssemblyDocumentNodeKey
      ?.get(documentNodeKey)
      ?.runtimeAssemblyNodeId ?? null
  }

  /**
   * 按仍属于当前结构的 nodeKey 投影一个新的不可变状态。
   *
   * 该入口供删除、working document 对账和历史恢复使用。binding 自身是不可变值，可以安全复用；
   * Map 容器必须重新创建，避免结构事务原地污染旧快照。
   *
   * @param state 当前项目运行时状态。
   * @param retainedNodeKeys 结构事务完成后仍有效的节点 key。
   * @returns 使用新 Map 持有的运行时状态。
   */
  public retain(
    state: PuppyCAD.ProjectModelRuntimeState,
    retainedNodeKeys: ReadonlySet<string>,
  ): PuppyCAD.ProjectModelRuntimeState {
    const bindingsByNodeKey = new Map<string, PuppyCAD.ProjectModelRuntimeBinding>()
    state.bindingsByNodeKey.forEach((binding, nodeKey) => {
      if (retainedNodeKeys.has(nodeKey)) {
        bindingsByNodeKey.set(nodeKey, binding)
      }
    })
    return Object.freeze({
      rootNodeKey: state.rootNodeKey,
      bindingsByNodeKey,
      bindingsByAssemblyDocumentNodeKey: this.createAssemblyDocumentBindingIndex(bindingsByNodeKey),
    })
  }

  /**
   * 用一个已经完成 finalization 的新子树状态替换当前子树绑定。
   *
   * replacement 可以来自整棵最终场景状态；该方法只消费 targetNodeKey 作用域内的 binding。
   * canonical 槽位未变化时继续继承 current 中的工作文档反向身份，使连续局部切换仍能从
   * assembly document key 唯一回到同一个项目节点。
   *
   * @param current 当前完整项目状态。
   * @param replacement 包含新子树 finalization 结果的运行时状态。
   * @param targetNodeKey 被替换的子树根 key。
   * @returns 仍以当前项目根为根、但拥有新子树绑定的状态。
   */
  public replaceSubtree(
    current: PuppyCAD.ProjectModelRuntimeState,
    replacement: PuppyCAD.ProjectModelRuntimeState,
    targetNodeKey: string,
  ): PuppyCAD.ProjectModelRuntimeState {
    const bindingsByNodeKey = new Map<string, PuppyCAD.ProjectModelRuntimeBinding>()
    current.bindingsByNodeKey.forEach((binding, nodeKey) => {
      if (!this.isNodeInsideSubtree(nodeKey, targetNodeKey)) {
        bindingsByNodeKey.set(nodeKey, binding)
      }
    })
    replacement.bindingsByNodeKey.forEach((binding, nodeKey) => {
      if (!this.isNodeInsideSubtree(nodeKey, targetNodeKey)) {
        return
      }

      const currentBinding = current.bindingsByNodeKey.get(nodeKey)
      const bindingWithDocumentIdentity = !binding.assemblyDocumentNodeKey
        && currentBinding?.assemblyDocumentNodeKey
        ? Object.freeze({
            ...binding,
            // canonical 槽位未变化时，局部 finalization 只更新运行时对象，不得丢失工作文档反向身份。
            assemblyDocumentNodeKey: currentBinding.assemblyDocumentNodeKey,
          })
        : binding
      this.addBinding(bindingsByNodeKey, bindingWithDocumentIdentity)
    })
    this.assertUniqueOccurrenceOwnership(bindingsByNodeKey)
    return Object.freeze({
      rootNodeKey: current.rootNodeKey,
      bindingsByNodeKey,
      bindingsByAssemblyDocumentNodeKey: this.createAssemblyDocumentBindingIndex(bindingsByNodeKey),
    })
  }

  /**
   * 把一个新加载子树的绑定追加到当前项目状态。
   *
   * @param current 当前完整项目状态。
   * @param appended 新加载子树状态。
   * @returns 使用新 Map 持有的合并状态。
   */
  public append(
    current: PuppyCAD.ProjectModelRuntimeState,
    appended: PuppyCAD.ProjectModelRuntimeState,
  ): PuppyCAD.ProjectModelRuntimeState {
    const bindingsByNodeKey = new Map(current.bindingsByNodeKey)
    appended.bindingsByNodeKey.forEach((binding) => {
      this.addBinding(bindingsByNodeKey, binding)
    })
    this.assertUniqueOccurrenceOwnership(bindingsByNodeKey)
    return Object.freeze({
      rootNodeKey: current.rootNodeKey,
      bindingsByNodeKey,
      bindingsByAssemblyDocumentNodeKey: this.createAssemblyDocumentBindingIndex(bindingsByNodeKey),
    })
  }

  /**
   * 一次性构建项目运行时状态和兼容场景绑定。
   *
   * @param input 最终 hierarchy、场景索引与可选 complete 上下文。
   * @returns 可原子写入 ProjectModelLoadResult 的解析结果。
   */
  public resolve(
    input: PuppyCAD.ProjectModelRuntimeStateResolveInput,
  ): PuppyCAD.ProjectModelRuntimeStateResolution {
    const businessNodeByObjectId = new Map<string, GlbHierarchyNode>()
    const hierarchyByObjectId = this.createHierarchyIndex(
      input.runtimeHierarchy,
      businessNodeByObjectId,
    )
    const bindingsByNodeKey = new Map<string, PuppyCAD.ProjectModelRuntimeBinding>()
    const sceneBindings: ProjectModelSceneBinding[] = []
    const sceneBindingKeys = new Set<string>()

    Object.entries(input.sceneBindings.byProjectNodeKey ?? {}).forEach(([nodeKey, objectIds]) => {
      const componentRoot = this.resolveProjectComponentRoot(
        nodeKey,
        objectIds,
        hierarchyByObjectId,
      )
      this.addBinding(bindingsByNodeKey, this.createRuntimeBinding(nodeKey, componentRoot))
      objectIds.forEach((objectId) => {
        this.addSceneBinding(sceneBindings, sceneBindingKeys, nodeKey, objectId)
      })
    })

    if (input.completeContext) {
      this.appendCompleteBindings({
        context: input.completeContext,
        businessNodeByObjectId,
        bindingsByNodeKey,
        sceneBindings,
        sceneBindingKeys,
      })
    }

    if (input.assemblyDocumentContext) {
      this.appendAssemblyDocumentBindings({
        context: input.assemblyDocumentContext,
        bindingsByNodeKey,
      })
    }

    if (!bindingsByNodeKey.has(input.rootNodeKey)) {
      throw new Error(
        `项目运行时状态缺少根节点绑定：rootNodeKey=${input.rootNodeKey}`,
      )
    }
    this.assertUniqueOccurrenceOwnership(bindingsByNodeKey)

    return Object.freeze({
      runtimeState: Object.freeze({
        rootNodeKey: input.rootNodeKey,
        bindingsByNodeKey,
        bindingsByAssemblyDocumentNodeKey: this.createAssemblyDocumentBindingIndex(bindingsByNodeKey),
      }),
      sceneBindings: Object.freeze(sceneBindings),
    })
  }

  /**
   * 把已物化项目树中的 canonical 节点绑定到工作装配文档节点。
   *
   * `.puppy` 重载后的项目树 key 来自 occurrenceId，工作文档仍使用后端结构 key。
   * 匹配必须限制在同一父装配下并使用稳定实例 ID，禁止通过名称或 definition ID 猜测实例。
   *
   * @param input 工作装配文档上下文与已建立的 canonical 场景绑定。
   */
  private appendAssemblyDocumentBindings(input: {
    readonly context: PuppyCAD.ProjectModelAssemblyDocumentRuntimeStateContext
    readonly bindingsByNodeKey: Map<string, PuppyCAD.ProjectModelRuntimeBinding>
  }): void {
    /**
     * 递归对齐同一个父装配下的模型节点与文档节点。
     *
     * @param projectParent 当前项目模型父节点。
     * @param documentParentNodeKey 当前工作装配文档父节点 key。
     */
    const visitChildren = (
      projectParent: ModelAssetNode,
      documentParentNodeKey: string,
    ): void => {
      const documentChildren = listAssemblyDocumentChildNodes(
        input.context.assemblyDocument,
        documentParentNodeKey,
      )
      const documentNodeByKey = new Map(documentChildren.map(node => [node.nodeKey, node]))
      const documentNodeByInstanceId = new Map<string, typeof documentChildren[number]>()

      documentChildren.forEach((documentNode) => {
        const instanceId = documentNode.instanceId?.trim() ?? ''
        if (!instanceId) {
          return
        }
        const existing = documentNodeByInstanceId.get(instanceId)
        if (existing) {
          throw new Error(
            '同一父装配下存在重复的工作文档 instance_id：'
            + `parentNodeKey=${documentParentNodeKey}, instanceId=${instanceId}, `
            + `firstNodeKey=${existing.nodeKey}, duplicateNodeKey=${documentNode.nodeKey}`,
          )
        }
        documentNodeByInstanceId.set(instanceId, documentNode)
      })

      const claimedDocumentNodeKeys = new Set<string>()
      projectParent.children.forEach((projectNode) => {
        const canonicalBinding = input.bindingsByNodeKey.get(projectNode.nodeKey)
        const projectInstanceId = projectNode.instanceUuid?.trim() ?? ''
        const keyMatchedNode = documentNodeByKey.get(projectNode.nodeKey)
        const instanceMatchedNode = projectInstanceId
          ? documentNodeByInstanceId.get(projectInstanceId)
          : undefined

        if (keyMatchedNode && instanceMatchedNode && keyMatchedNode.nodeKey !== instanceMatchedNode.nodeKey) {
          throw new Error(
            '项目节点 key 与 instance_id 指向不同工作装配节点：'
            + `nodeKey=${projectNode.nodeKey}, instanceId=${projectInstanceId}, `
            + `keyMatchedNodeKey=${keyMatchedNode.nodeKey}, `
            + `instanceMatchedNodeKey=${instanceMatchedNode.nodeKey}`,
          )
        }

        const documentNode = instanceMatchedNode ?? keyMatchedNode
        if (!documentNode) {
          // 包内 occurrence 不一定进入根工作文档；没有明确桥接时保留 canonical binding，
          // 但禁止通过名称或共享 definition ID 把它误认成另一个工作 occurrence。
          return
        }
        const documentInstanceId = documentNode.instanceId?.trim() ?? ''
        if (
          keyMatchedNode
          && projectInstanceId
          && documentInstanceId
          && projectInstanceId !== documentInstanceId
        ) {
          throw new Error(
            '项目节点与工作装配节点 instance_id 不一致：'
            + `nodeKey=${projectNode.nodeKey}, projectInstanceId=${projectInstanceId}, `
            + `documentInstanceId=${documentInstanceId}`,
          )
        }
        if (claimedDocumentNodeKeys.has(documentNode.nodeKey)) {
          throw new Error(
            '同一工作装配节点被多个同级项目节点认领：'
            + `parentNodeKey=${documentParentNodeKey}, `
            + `assemblyDocumentNodeKey=${documentNode.nodeKey}`,
          )
        }
        if (projectNode.nodeType !== documentNode.nodeType) {
          throw new Error(
            '项目节点与工作装配节点类型不一致：'
            + `nodeKey=${projectNode.nodeKey}, assemblyDocumentNodeKey=${documentNode.nodeKey}, `
            + `projectType=${projectNode.nodeType}, documentType=${documentNode.nodeType}`,
          )
        }
        if (!canonicalBinding) {
          throw new Error(
            '工作装配节点对应的项目节点缺少 canonical 场景绑定：'
            + `nodeKey=${projectNode.nodeKey}, assemblyDocumentNodeKey=${documentNode.nodeKey}`,
          )
        }

        claimedDocumentNodeKeys.add(documentNode.nodeKey)
        this.addBinding(input.bindingsByNodeKey, Object.freeze({
          ...canonicalBinding,
          assemblyDocumentNodeKey: documentNode.nodeKey,
        }))

        if (projectNode.nodeType === 'assembly') {
          visitChildren(projectNode, documentNode.nodeKey)
        }
      })
    }

    visitChildren(input.context.projectRoot, input.context.projectRoot.nodeKey)
  }

  /**
   * 建立 hierarchy 的对象 UUID 索引。
   *
   * @param root 最终项目 hierarchy 根。
   * @returns 对象 UUID 到 hierarchy 节点的唯一映射。
   */
  private createHierarchyIndex(
    root: GlbHierarchyNode,
    businessNodeByObjectId: Map<string, GlbHierarchyNode>,
  ): ReadonlyMap<string, GlbHierarchyNode> {
    const byObjectId = new Map<string, GlbHierarchyNode>()
    const visit = (node: GlbHierarchyNode): void => {
      if (byObjectId.has(node.objectId)) {
        throw new Error(`项目运行时 hierarchy 对象 UUID 重复：objectId=${node.objectId}`)
      }
      byObjectId.set(node.objectId, node)
      if (this.isBusinessNode(node)) {
        businessNodeByObjectId.set(node.objectId, node)
      }
      node.children.forEach(visit)
    }
    visit(root)
    return byObjectId
  }

  /**
   * 从一个项目 nodeKey 的对象集合中选出最外层业务组件根。
   *
   * 项目组合器会同时绑定 nodeGroup 与其挂载包对象；nodeGroup 是唯一一个父对象不在同一绑定集合
   * 中的业务节点，因此无需依赖数组顺序、对象名称或 Three.js 具体类型。
   *
   * @param nodeKey 当前项目节点 key。
   * @param objectIds 当前节点的场景对象集合。
   * @param hierarchyByObjectId 最终 hierarchy 索引。
   * @returns 唯一业务组件根。
   */
  private resolveProjectComponentRoot(
    nodeKey: string,
    objectIds: readonly string[],
    hierarchyByObjectId: ReadonlyMap<string, GlbHierarchyNode>,
  ): GlbHierarchyNode {
    const objectIdSet = new Set(objectIds)
    const candidates = objectIds
      .map(objectId => hierarchyByObjectId.get(objectId))
      .filter((node): node is GlbHierarchyNode => Boolean(
        node
        && this.isBusinessNode(node)
        && !objectIdSet.has(node.parentObjectId ?? ''),
      ))

    if (candidates.length !== 1) {
      throw new Error(
        '项目节点运行时组件无法唯一定位：'
        + `nodeKey=${nodeKey}, objectIds=${objectIds.join(',')}, candidates=${candidates.length}`,
      )
    }
    return candidates[0]!
  }

  /**
   * 把 complete GLB 已覆盖的工作装配节点补充到统一状态。
   *
   * @param input complete 上下文、最终 hierarchy 与当前解析结果。
   */
  private appendCompleteBindings(input: {
    readonly context: PuppyCAD.ProjectModelCompleteRuntimeStateContext
    readonly businessNodeByObjectId: ReadonlyMap<string, GlbHierarchyNode>
    readonly bindingsByNodeKey: Map<string, PuppyCAD.ProjectModelRuntimeBinding>
    readonly sceneBindings: ProjectModelSceneBinding[]
    readonly sceneBindingKeys: Set<string>
  }): void {
    const completeResult = input.context.completePackageResult.result
    if (completeResult.kind !== 'composed-assembly') {
      throw new Error(
        '项目 complete 包不是 composed-assembly：'
        + `packageFileId=${input.context.completePackageResult.file.fileId}, kind=${completeResult.kind}`,
      )
    }

    const materializedNodeByInstanceId = this.createMaterializedNodeByCompleteInstanceId(
      input.context,
    )
    const seenInstanceIds = new Set<string>()

    /**
     * 工作装配文档保留后端结构 key，物化项目树则使用 occurrenceId 派生 key。
     * 两棵树只能通过稳定 instance_id 对齐；递归过程同时保证同一 occurrence 不会跨层重复认领。
     */
    const visitDocumentChildren = (parentDocumentNodeKey: string): void => {
      const children = listAssemblyDocumentChildNodes(
        input.context.assemblyDocument,
        parentDocumentNodeKey,
      )
      children.forEach((documentNode) => {
        if (input.context.packageRootNodeKeys.has(documentNode.nodeKey)) {
          // instance_id 只在单个归档包内唯一；其他包根及其后代由各自 package result 负责绑定。
          return
        }
        const sourceInstanceId = documentNode.instanceId?.trim() ?? ''
        const descriptor = sourceInstanceId
          ? input.context.occurrenceGraph.occurrencesByInstanceId.get(sourceInstanceId)
          : undefined
        if (!descriptor) {
          return
        }
        if (seenInstanceIds.has(sourceInstanceId)) {
          throw new Error(
            '项目 complete occurrence instance_id 重复：'
            + `projectNodeKey=${input.context.projectNodeKey}, instanceId=${sourceInstanceId}`,
          )
        }
        seenInstanceIds.add(sourceInstanceId)
        if (descriptor.semanticKind !== documentNode.nodeType) {
          throw new Error(
            '项目 complete occurrence 类型不一致：'
            + `nodeKey=${documentNode.nodeKey}, instanceId=${sourceInstanceId}, `
            + `documentType=${documentNode.nodeType}, occurrenceType=${descriptor.semanticKind}`,
          )
        }

        const sourceObjectIds = completeResult.sceneBindings.byAssemblyNodeId[sourceInstanceId] ?? []
        if (sourceObjectIds.length === 0) {
          throw new Error(
            '项目 complete occurrence 缺少源场景对象绑定：'
            + `nodeKey=${documentNode.nodeKey}, instanceId=${sourceInstanceId}`,
          )
        }
        const componentNodes = [...new Set(sourceObjectIds)]
          .map(objectId => input.businessNodeByObjectId.get(objectId))
          .filter((node): node is GlbHierarchyNode => Boolean(node))
        if (componentNodes.length !== 1) {
          throw new Error(
            '项目 complete occurrence 运行时组件无法唯一定位：'
            + `nodeKey=${documentNode.nodeKey}, instanceId=${sourceInstanceId}, `
            + `candidates=${componentNodes.length}`,
          )
        }

        const canonicalNodeKey = materializedNodeByInstanceId.get(sourceInstanceId)?.nodeKey
          ?? documentNode.nodeKey
        const binding = this.createRuntimeBinding(
          canonicalNodeKey,
          componentNodes[0]!,
          documentNode.nodeKey,
        )
        this.addBinding(input.bindingsByNodeKey, binding)
        this.addSceneBinding(
          input.sceneBindings,
          input.sceneBindingKeys,
          canonicalNodeKey,
          binding.componentRootObjectId,
        )
        if (binding.selectionObjectId) {
          this.addSceneBinding(
            input.sceneBindings,
            input.sceneBindingKeys,
            canonicalNodeKey,
            binding.selectionObjectId,
          )
        }
        if (descriptor.semanticKind === 'assembly') {
          visitDocumentChildren(documentNode.nodeKey)
        }
      })
    }

    visitDocumentChildren(input.context.projectNodeKey)
  }

  /**
   * 建立 complete occurrence instance_id 到离线包物化节点的唯一索引。
   *
   * 历史 project-model 可能没有 instanceId，此时 `assemblyNodeId` 仍是包内 occurrence
   * 与 complete manifest 的显式桥梁；两者同时命中不同 occurrence 时必须拒绝加载。
   *
   * @param context complete 归档与已物化项目树上下文。
   * @returns 仅包含当前 complete occurrence graph 已声明节点的索引。
   */
  private createMaterializedNodeByCompleteInstanceId(
    context: PuppyCAD.ProjectModelCompleteRuntimeStateContext,
  ): ReadonlyMap<string, ModelAssetNode> {
    const nodeByInstanceId = new Map<string, ModelAssetNode>()
    const visit = (node: ModelAssetNode): void => {
      const source = node.assetSource
      if (
        source?.kind === 'embedded-puppy-occurrence'
        && source.containerNodeKey === context.projectNodeKey
      ) {
        const candidateInstanceIds = new Set(
          [node.instanceUuid?.trim(), source.assemblyNodeId.trim()]
            .filter((candidate): candidate is string => Boolean(
              candidate
              && context.occurrenceGraph.occurrencesByInstanceId.has(candidate),
            )),
        )
        if (candidateInstanceIds.size > 1) {
          throw new Error(
            '项目 complete 物化 occurrence 身份冲突：'
            + `nodeKey=${node.nodeKey}, candidates=${[...candidateInstanceIds].join(',')}`,
          )
        }
        const sourceInstanceId = [...candidateInstanceIds][0]
        if (sourceInstanceId) {
          const existing = nodeByInstanceId.get(sourceInstanceId)
          if (existing && existing.nodeKey !== node.nodeKey) {
            throw new Error(
              '项目 complete occurrence 被多个物化节点认领：'
              + `instanceId=${sourceInstanceId}, `
              + `firstNodeKey=${existing.nodeKey}, duplicateNodeKey=${node.nodeKey}`,
            )
          }
          nodeByInstanceId.set(sourceInstanceId, node)
        }
      }
      node.children.forEach(visit)
    }
    visit(context.projectRoot)
    return nodeByInstanceId
  }

  /**
   * 判断 hierarchy 节点是否为显式业务组件。
   *
   * @param node 当前 hierarchy 节点。
   * @returns node_type 明确为 assembly/part 时返回 true。
   */
  private isBusinessNode(node: GlbHierarchyNode): boolean {
    return node.meta?.semanticKind === 'assembly' || node.meta?.semanticKind === 'part'
  }

  /**
   * 从业务组件根创建通用运行时绑定。
   *
   * @param nodeKey 项目/工作装配节点 key。
   * @param componentRoot 最终业务组件根。
   * @returns 不包含 complete 专有字段的运行时绑定。
   */
  private createRuntimeBinding(
    nodeKey: string,
    componentRoot: GlbHierarchyNode,
    assemblyDocumentNodeKey?: string,
  ): PuppyCAD.ProjectModelRuntimeBinding {
    const runtimeAssemblyNodeId = componentRoot.meta?.assemblyNodeId?.trim()
    if (!runtimeAssemblyNodeId) {
      throw new Error(
        `项目运行时组件缺少装配身份：nodeKey=${nodeKey}, objectId=${componentRoot.objectId}`,
      )
    }
    const selectionObjectId = componentRoot.meta?.selectionObjectId?.trim()
    return Object.freeze({
      nodeKey,
      runtimeAssemblyNodeId,
      componentRootObjectId: componentRoot.objectId,
      ...(selectionObjectId ? { selectionObjectId } : {}),
      ...(assemblyDocumentNodeKey ? { assemblyDocumentNodeKey } : {}),
    })
  }

  /**
   * 向运行时状态加入唯一 nodeKey 绑定。
   *
   * @param bindings 当前状态 Map。
   * @param binding 待加入绑定。
   */
  private addBinding(
    bindings: Map<string, PuppyCAD.ProjectModelRuntimeBinding>,
    binding: PuppyCAD.ProjectModelRuntimeBinding,
  ): void {
    const existing = bindings.get(binding.nodeKey)
    if (!existing) {
      bindings.set(binding.nodeKey, binding)
      return
    }
    if (
      existing.runtimeAssemblyNodeId !== binding.runtimeAssemblyNodeId
      || existing.componentRootObjectId !== binding.componentRootObjectId
      || existing.selectionObjectId !== binding.selectionObjectId
      || (
        existing.assemblyDocumentNodeKey
        && binding.assemblyDocumentNodeKey
        && existing.assemblyDocumentNodeKey !== binding.assemblyDocumentNodeKey
      )
    ) {
      throw new Error(
        '项目节点存在不一致的运行时绑定：'
        + `nodeKey=${binding.nodeKey}, `
        + `existingAssemblyNodeId=${existing.runtimeAssemblyNodeId}, `
        + `nextAssemblyNodeId=${binding.runtimeAssemblyNodeId}`,
      )
    }
    if (!existing.assemblyDocumentNodeKey && binding.assemblyDocumentNodeKey) {
      bindings.set(binding.nodeKey, Object.freeze({
        ...existing,
        assemblyDocumentNodeKey: binding.assemblyDocumentNodeKey,
      }))
    }
  }

  /**
   * 从 canonical nodeKey 绑定构建工作装配文档 key 的反向索引。
   *
   * 离线包项目模型树与工作装配文档可以使用不同 key 空间，二者只能通过 finalization
   * 已确认的 instance binding 对齐。反向索引在状态发布时一次性构建，避免 hover、保存和版本切换
   * 热路径反复扫描项目树或按名称猜测。
   *
   * @param bindings 已完成唯一性校验的 canonical binding。
   * @returns 工作装配文档 key 到同一 binding 的只读映射。
   */
  private createAssemblyDocumentBindingIndex(
    bindings: ReadonlyMap<string, PuppyCAD.ProjectModelRuntimeBinding>,
  ): ReadonlyMap<string, PuppyCAD.ProjectModelRuntimeBinding> {
    const bindingsByAssemblyDocumentNodeKey = new Map<
      string,
      PuppyCAD.ProjectModelRuntimeBinding
    >()

    bindings.forEach((binding) => {
      const assemblyDocumentNodeKey = binding.assemblyDocumentNodeKey?.trim() ?? ''
      if (!assemblyDocumentNodeKey) {
        return
      }

      const existing = bindingsByAssemblyDocumentNodeKey.get(assemblyDocumentNodeKey)
      if (existing && existing.nodeKey !== binding.nodeKey) {
        throw new Error(
          '工作装配节点被多个项目节点认领：'
          + `assemblyDocumentNodeKey=${assemblyDocumentNodeKey}, `
          + `firstNodeKey=${existing.nodeKey}, duplicateNodeKey=${binding.nodeKey}`,
        )
      }
      bindingsByAssemblyDocumentNodeKey.set(assemblyDocumentNodeKey, binding)
    })

    return bindingsByAssemblyDocumentNodeKey
  }

  /**
   * 校验每个运行时 occurrence 和组件根只属于一个项目节点。
   *
   * `instance_id` 可以在不同加载单元中重复，但 compose 后的 occurrence 身份和 Object3D 根必须
   * 已经按项目作用域重绑为唯一值。允许两个 nodeKey 认领同一根会让高亮、移动与提交发生串扰。
   *
   * @param bindings 已完成 nodeKey 合并的项目运行时绑定。
   */
  private assertUniqueOccurrenceOwnership(
    bindings: ReadonlyMap<string, PuppyCAD.ProjectModelRuntimeBinding>,
  ): void {
    const ownerByAssemblyNodeId = new Map<string, string>()
    const ownerByComponentRootObjectId = new Map<string, string>()

    bindings.forEach((binding, nodeKey) => {
      const assemblyOwner = ownerByAssemblyNodeId.get(binding.runtimeAssemblyNodeId)
      const componentOwner = ownerByComponentRootObjectId.get(binding.componentRootObjectId)
      if (
        (assemblyOwner && assemblyOwner !== nodeKey)
        || (componentOwner && componentOwner !== nodeKey)
      ) {
        throw new Error(
          '项目运行时 occurrence 被多个节点认领：'
          + `nodeKey=${nodeKey}, existingNodeKey=${assemblyOwner ?? componentOwner}, `
          + `assemblyNodeId=${binding.runtimeAssemblyNodeId}, `
          + `componentRootObjectId=${binding.componentRootObjectId}`,
        )
      }
      ownerByAssemblyNodeId.set(binding.runtimeAssemblyNodeId, nodeKey)
      ownerByComponentRootObjectId.set(binding.componentRootObjectId, nodeKey)
    })
  }

  /**
   * 追加去重后的兼容场景绑定。
   *
   * @param bindings 当前兼容绑定列表。
   * @param keys 当前去重索引。
   * @param nodeKey 项目/工作装配节点 key。
   * @param objectId 场景对象 UUID。
   */
  private addSceneBinding(
    bindings: ProjectModelSceneBinding[],
    keys: Set<string>,
    nodeKey: string,
    objectId: string,
  ): void {
    const key = `${nodeKey}\u0000${objectId}`
    if (keys.has(key)) {
      return
    }
    keys.add(key)
    bindings.push(Object.freeze({ nodeKey, objectId }))
  }

  /**
   * 判断节点是否属于给定 nodeKey 子树。
   *
   * @param nodeKey 待判断节点 key。
   * @param subtreeRootNodeKey 子树根 key。
   * @returns 节点等于根或以根路径为前缀时返回 true。
   */
  private isNodeInsideSubtree(nodeKey: string, subtreeRootNodeKey: string): boolean {
    return nodeKey === subtreeRootNodeKey || nodeKey.startsWith(`${subtreeRootNodeKey}/`)
  }
}
